import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';

import { AuthService } from '../../features/auth/services/auth.service';
import { PermissionService } from '../services/permission.service';

export const authGuard: CanActivateFn = (route, state): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const resolveUnauthorizedRedirect = (): UrlTree => {
    // HOME is the always-accessible main view, so it is the safe fallback for any
    // route the user is not authorized to open.
    return router.createUrlTree(['/home']);
  };

  const evaluateAccess = (): boolean | UrlTree => {
    try {
      const isAuthenticated = authService.isAuthenticated();
      const hasValidSession = authService.hasValidSession();
      const accessToken = authService.getAccessToken();

      if (!isAuthenticated || !hasValidSession || !accessToken) {
        if (state.url.startsWith('/login') || state.url.startsWith('/auth/login')) {
          return true;
        }

        return router.createUrlTree(['/login'], {
          queryParams: { redirectTo: state.url }
        });
      }

      const canAccessRoute = permissionService.canAccessRouteData(route.data);

      if (canAccessRoute) {
        return true;
      }

      return resolveUnauthorizedRedirect();
    } catch {
      return router.createUrlTree(['/login'], {
        queryParams: { redirectTo: state.url }
      });
    }
  };

  if (!authService.isInitialized()) {
    return authService.waitForInitialization().pipe(map(() => evaluateAccess()));
  }

  return evaluateAccess();
};
